// Shared AES-GCM helpers for encryptData.js and decryptData.js.

import { assembleValue } from "../services/examDetailsService";
import { ALGORITHMS } from "../config/algorithm";

export const AES_GCM_IV_BYTES = 12;
export const AES_GCM_TAG_BITS = 128;
export const AES_GCM_TAG_BYTES = AES_GCM_TAG_BITS / 8;

export const base64ToBytes = (base64) =>
  Uint8Array.from(
    atob(base64),
    (character) => character.charCodeAt(0)
  );

export const bytesToBase64 = (bytes) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)));

export const hexToBytes = (hex) =>
  Uint8Array.from(hex.match(/.{1,2}/g).map((byte) => parseInt(byte, 16)));

export const bytesToHex = (bytes) =>
  Array.from(new Uint8Array(bytes))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");

/**
 * @param {Uint8Array} rawKeyBytes
 * @param {"encrypt"|"decrypt"} usage
 */
export const importAesGcmKey = (rawKeyBytes, usage) =>
  window.crypto.subtle.importKey(
    "raw",
    rawKeyBytes,
    { name: "AES-GCM" },
    false,
    [usage]
  );


//IV and TAG_BYTES synced with server side packData
export const unpackEncData = (packedBase64) => {
  const packedBytes = base64ToBytes(packedBase64);

  const minimumLength =
    AES_GCM_IV_BYTES + AES_GCM_TAG_BYTES;

  if (packedBytes.length <= minimumLength) {
    throw new Error("Invalid packed AES-GCM data");
  }

  return {
    iv: packedBytes.slice(0, AES_GCM_IV_BYTES),

    authTag: packedBytes.slice(
      AES_GCM_IV_BYTES,
      minimumLength
    ),

    encryptedData: packedBytes.slice(minimumLength), 
  };
};
  
export const packEncData = ({ iv, authTag, encryptedData }) => {
  const packedBytes = new Uint8Array([
    ...base64ToBytes(iv),
    ...base64ToBytes(authTag),
    ...base64ToBytes(encryptedData),
  ]);
  return bytesToBase64(packedBytes);
};

export const  getAppKeyBytes = () => hexToBytes(assembleValue());

export const encryptData = async (rawKeyHex, plainKeyBytes) => {
  const rawKeyBytes = hexToBytes(rawKeyHex);
  const cryptoKey = await importAesGcmKey(rawKeyBytes, "encrypt");
  const iv = window.crypto.getRandomValues(new Uint8Array(AES_GCM_IV_BYTES));
  const cipherWithTag = new Uint8Array(
    await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv, tagLength: AES_GCM_TAG_BITS },
      cryptoKey,
      plainKeyBytes
    )
  );

  const ciphertextBytes = cipherWithTag.slice(
    0,
    cipherWithTag.length - AES_GCM_TAG_BYTES
  );
  const authTagBytes = cipherWithTag.slice(
    cipherWithTag.length - AES_GCM_TAG_BYTES
  );

  // Packed to match the server's unpackEncryptedData (iv + authTag +
  // encryptedData, concatenated then base64-encoded once) — mirrors
  // decryptData(), which takes this same packed shape.
  return packEncData({
    encryptedData: bytesToBase64(ciphertextBytes),
    iv: bytesToBase64(iv),
    authTag: bytesToBase64(authTagBytes),
  });
};

export const encryptKeyFromUtf8 = async (rawKeyBytes, plainKey) =>
  // encryptData now takes its key as hex — bridge the raw bytes callers
  // already pass here (e.g. wrapKey()'s decrypted reqResDek) to that.
  encryptData(bytesToHex(rawKeyBytes), new TextEncoder().encode(plainKey));

export const decryptData = async (rawData, packedEncryptedKey) => { // to-do-11
  // rawData is the key as hex (mirrors encryptData) — converted to bytes here.
  const rawKeyBytes = hexToBytes(rawData);
  const envelope = unpackEncData(packedEncryptedKey);
  const cryptoKey = await importAesGcmKey(rawKeyBytes, "decrypt");
  const ciphertextWithTag = new Uint8Array([
    ...envelope.encryptedData,
    ...envelope.authTag,
  ]);

  const plaintext = await window.crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: envelope.iv,
      tagLength: AES_GCM_TAG_BITS,
    },
    cryptoKey,
    ciphertextWithTag
  );

  return new Uint8Array(plaintext);
};

export const decryptKeyToUtf8 = async (rawKeyBytes, packedEncryptedKey) =>
  new TextDecoder("utf-8").decode(
    await decryptData(bytesToHex(rawKeyBytes), packedEncryptedKey)
  );


/**
* Decrypts asymmetrically encrypted data with the client device's private key.
*/
export const asymDecrypt = async (
  encData,
  clientDevicePvtKey,
  algoConfig = ALGORITHMS.RSA_2048_OAEP_SHA256
) => {
  const pemBody = clientDevicePvtKey
    .replace(/-----(BEGIN|END)[^-]+-----/g, "")
    .replace(/\s+/g, "");

  const privateKey = await window.crypto.subtle.importKey(
    "pkcs8",
    base64ToBytes(pemBody),
    { name: algoConfig.name, hash: algoConfig.oaepHash },
    false,
    ["decrypt"]
  );

  const plaintext = await window.crypto.subtle.decrypt(
    { name: algoConfig.name },
    privateKey,
    base64ToBytes(encData)
  );

  return new Uint8Array(plaintext);
};
